/** @jsxImportSource @emotion/react */
import Button from 'components/atoms/Button'
import Toast from 'components/atoms/Toast'
import DobVerify from 'components/landing/hstPaperwork/Verify'
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLazyGetPaymentReceiptDownloadQuery } from 'store/services/paymentReceipts'

const ReceiptPortal: React.FC = () => {
  const [params] = useSearchParams()
  const receiptId = params.get('receiptId') || ''
  const patientId = Number(params.get('patientId') || 0)
  const [verified, setVerified] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [getDownloadUrl] = useLazyGetPaymentReceiptDownloadQuery()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleDownload = async () => {
    setIsDownloading(true)

    try {
      const link = await getDownloadUrl({ receiptId, patientId, download: true }).unwrap()

      if (link?.url) {
        // Not window.open: the URL only arrives after an await, so the click
        // that opened it is no longer the active gesture and mobile web views
        // block the popup. A same-tab anchor is never blocked, and the
        // attachment disposition means the browser saves the file instead of
        // navigating away from the portal.
        const anchor = document.createElement('a')

        anchor.href = link.url
        anchor.rel = 'noopener'
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
      }
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to open receipt'
      Toast({ type: 'error', label: message })
    } finally {
      setIsDownloading(false)
    }
  }

  if (!receiptId || !patientId) {
    return <div className="container py-5">Invalid link</div>
  }

  if (!verified) {
    return (
      <div className="container py-5" css={{ maxWidth: 720 }}>
        <DobVerify
          receiptId={receiptId}
          nextStep={() => setVerified(true)}
          title={'TestSmarter Payment Receipt'}
        />
      </div>
    )
  }

  return (
    <div className="container py-5" css={{ maxWidth: 480 }}>
      <div css={{ textAlign: 'center', marginBottom: 40 }}>
        <h1 css={{ fontSize: 36, fontWeight: 700, color: '#2c3e50', marginBottom: 8 }}>
          Payment Receipt
        </h1>
        <div css={{ fontSize: 18, color: '#5f6368' }}>Test Smarter</div>
      </div>

      <div
        css={{
          backgroundColor: '#fff',
          borderRadius: 8,
          border: '1px solid #e6e8eb',
          padding: 24,
          textAlign: 'center',
        }}>
        <p css={{ fontSize: 16, color: '#5f6368', marginBottom: 24 }}>
          Your receipt is ready. No payment is due &mdash; this is a record of activity on your
          account.
        </p>
        <Button
          className="btn-primary"
          label={isDownloading ? 'Opening...' : 'View Receipt'}
          onClick={handleDownload}
          disabled={isDownloading}
        />
      </div>

      <div css={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#5f6368' }}>
        Questions? Call us at (386) 382-4200.
      </div>
    </div>
  )
}

export default ReceiptPortal
